import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, ReplaySubject, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../api/api.service';
import { UserProfileService } from '../user/user-profile.service';
import { IContact, IOrganization, IPagination, ITag, TGroupType } from '../../types/contactModule';
import {
  IEntitySegmentQuery,
  filtersToCriteria,
} from '../../components/organization/segments/segment-utilities';

const SERVICE = 'blackkonnectcxpcontacts';

/** Member counts change when people are added or removed, so they expire. */
const CACHE_MS = 60_000;
/** Per-row counts load a few at a time so a page of groups never floods the API. */
const CONCURRENCY = 4;

export interface Page<T> {
  items: T[];
  itemPagination: IPagination;
}

/** A group as the members endpoints need it. */
export interface GroupRef {
  kind: TGroupType;
  uuid: string;
  /** Numeric id: tags filter the contact list by it. */
  id?: string | number | null;
}

/** Who in a group can receive an SMS. */
export interface Reach {
  withNumber: number;
  withoutNumber: number;
}

const EMPTY_PAGINATION: IPagination = {
  totalElements: 0,
  pageSize: 10,
  totalPages: 1,
  pageNumber: 0,
  offSet: 0,
};

/** Drops empty values: HttpParams would send them as "undefined" or "". */
function clean(params: Record<string, unknown>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && !value.length) continue;
    out[key] = value;
  }
  return out;
}

function toPage<T>(data: any, size?: number): Page<T> {
  const items = (Array.isArray(data) ? data : data?.items ?? []) as T[];
  const pagination = (data?.itemPagination as IPagination) ?? {
    ...EMPTY_PAGINATION,
    totalElements: items.length,
    pageSize: size ?? items.length ?? 10,
  };
  return { items, itemPagination: pagination };
}

function toCount(value: any): number {
  const raw = value !== null && typeof value === 'object' ? value.count ?? value.total : value;
  const count = Number(raw);
  return Number.isFinite(count) ? count : 0;
}

/**
 * Reads for organizations, tags and segments with no side effects on
 * ContactsService's shared lists and loading flags: each list and details
 * page keeps its own state. Writes still go through ContactsService.
 */
@Injectable({ providedIn: 'root' })
export class GroupAudienceService {
  private api = inject(ApiService);
  private user = inject(UserProfileService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private counts = new Map<string, { at: number; result: ReplaySubject<number> }>();
  private queue = new Subject<{ ref: GroupRef; key: string; result: ReplaySubject<number> }>();

  /** Tags whose contact-list filter turned out unsupported: list them the old way. */
  private legacyTagListing = new Set<string>();

  constructor() {
    this.queue
      .pipe(
        mergeMap(
          ({ ref, key, result }) =>
            this.loadCount(ref).pipe(
              tap((count) => {
                result.next(count);
                result.complete();
              }),
              catchError((error) => {
                this.counts.delete(key);
                result.error(error);
                return EMPTY;
              })
            ),
          CONCURRENCY
        )
      )
      .subscribe();
  }

  private get org(): string {
    return this.user.organizationId;
  }

  // ── Lists ──

  listOrganizations(params: Record<string, unknown>): Observable<Page<IOrganization>> {
    return this.api
      .get<any>(`${SERVICE}/api/v2/company-entities/organisations/${this.org}/companies`, clean(params))
      .pipe(map((data) => toPage<IOrganization>(data, params['size'] as number)));
  }

  listTags(params: Record<string, unknown>): Observable<Page<ITag>> {
    return this.api
      .get<any>(`${SERVICE}/api/v2/entity-tags/organisations/${this.org}`, clean(params))
      .pipe(map((data) => toPage<ITag>(data, params['size'] as number)));
  }

  listSegments(params: Record<string, unknown>): Observable<Page<IEntitySegmentQuery>> {
    return this.api
      .get<any>(`${SERVICE}/api/entity-segments/organisations/${this.org}`, clean(params))
      .pipe(map((data) => toPage<IEntitySegmentQuery>(data, params['size'] as number)));
  }

  // ── One group ──

  getOrganization(uuid: string): Observable<IOrganization> {
    return this.api.get<IOrganization>(`${SERVICE}/api/company-entities/organisations/${this.org}/companies/${uuid}`);
  }

  getTag(uuid: string): Observable<ITag> {
    return this.api.get<ITag>(`${SERVICE}/api/entity-tags/organisations/${this.org}/entity-tags/${uuid}`);
  }

  getSegment(uuid: string): Observable<IEntitySegmentQuery> {
    return this.api.get<IEntitySegmentQuery>(
      `${SERVICE}/api/entity-segments/organisations/${this.org}/entity-segments/${uuid}`
    );
  }

  // ── Member counts (cached, a few at a time) ──

  memberCount(ref: GroupRef): Observable<number> {
    if (!this.isBrowser || !ref?.uuid) return EMPTY;
    const key = `${ref.kind}:${ref.uuid}`;
    const cached = this.counts.get(key);
    if (cached && Date.now() - cached.at < CACHE_MS) return cached.result.asObservable();

    const result = new ReplaySubject<number>(1);
    this.counts.set(key, { at: Date.now(), result });
    this.queue.next({ ref, key, result });
    return result.asObservable();
  }

  /** Forget a cached count after members were added or removed. */
  invalidate(ref: GroupRef | null | undefined): void {
    if (ref?.uuid) this.counts.delete(`${ref.kind}:${ref.uuid}`);
  }

  private loadCount(ref: GroupRef): Observable<number> {
    let url: string;
    switch (ref.kind) {
      case 'organization':
        url = `${SERVICE}/api/person-entities/organisations/${this.org}/contacts/companies/${ref.uuid}/count`;
        break;
      case 'tag':
        url = `${SERVICE}/api/entity-tags/organisations/${this.org}/tags/${ref.uuid}/count`;
        break;
      default:
        url = `${SERVICE}/api/person-entities/organisations/${this.org}/entity-segments/${ref.uuid}/contacts/count`;
    }
    return this.api.get<any>(url).pipe(map(toCount));
  }

  // ── Members ──

  /**
   * One page of a group's members. Organizations and segments use their
   * members endpoints. Tags use the contact list filtered by tag, which
   * supports search; if that filter is ignored (rows without the tag come
   * back), the tag falls back to its own members endpoint.
   */
  membersPage(ref: GroupRef, params: Record<string, unknown>): Observable<Page<IContact>> {
    const size = params['size'] as number;
    if (ref.kind === 'tag' && ref.id != null && !this.legacyTagListing.has(ref.uuid)) {
      return this.api
        .get<any>(
          `${SERVICE}/api/v2/person-entities/organisations/${this.org}/contacts`,
          clean({ ...params, 'entityTagId.equals': ref.id })
        )
        .pipe(
          map((data) => toPage<IContact>(data, size)),
          switchMap((page) => {
            const strays = page.items.filter((contact) => !this.hasTag(contact, ref));
            if (!strays.length) return of(page);
            this.legacyTagListing.add(ref.uuid);
            return this.membersPage(ref, params);
          })
        );
    }
    return this.api
      .get<any>(this.membersUrl(ref), clean(params))
      .pipe(map((data) => toPage<IContact>(data, size)));
  }

  /** True when member search runs on the server for this group. */
  searchesOnServer(ref: GroupRef): boolean {
    return !(ref.kind === 'tag' && (ref.id == null || this.legacyTagListing.has(ref.uuid)));
  }

  private membersUrl(ref: GroupRef): string {
    switch (ref.kind) {
      case 'organization':
        return `${SERVICE}/api/v2/person-entities/organisations/${this.org}/contacts/companies/${ref.uuid}`;
      case 'tag':
        return `${SERVICE}/api/person-entities/organisations/${this.org}/contacts/tags/${ref.uuid}`;
      default:
        return `${SERVICE}/api/v2/person-entities/organisations/${this.org}/contacts/segments/${ref.uuid}`;
    }
  }

  private hasTag(contact: IContact, ref: GroupRef): boolean {
    const tags = contact?.entityTags;
    // Rows without tag data cannot be checked; trust the filter for them.
    if (!Array.isArray(tags)) return true;
    return tags.some((tag) => String(tag?.id) === String(ref.id) || (!!tag?.uuid && tag.uuid === ref.uuid));
  }

  // ── Reach: members with and without a phone number ──

  /**
   * Counts members with and without a phone number from the contact list
   * filtered by organization or tag. Returns null unless the two counts add
   * up to the group's own member count, so a filter the API ignores can
   * never show a wrong number.
   */
  reach(ref: GroupRef, memberTotal: number): Observable<Reach | null> {
    if (!this.isBrowser || ref.id == null || ref.kind === 'segment') return of(null);
    const groupFilter = ref.kind === 'organization' ? { 'companyEntityId.equals': ref.id } : { 'entityTagId.equals': ref.id };
    return this.reachFor(groupFilter, memberTotal);
  }

  /**
   * The same for a segment, from its rules as the contact list query that
   * matches them. Shown only when that query finds exactly the segment's
   * own member count, so an imperfect translation stays hidden.
   */
  segmentReach(segment: IEntitySegmentQuery, memberTotal: number): Observable<Reach | null> {
    if (!this.isBrowser) return of(null);
    const criteria = filtersToCriteria(segment?.entitySegmentQueryFilters, { asSaved: true });
    if (criteria.skipped || !Object.keys(criteria.params).length) return of(null);
    return this.countContacts(criteria.params).pipe(
      switchMap((total) => (total === memberTotal ? this.reachFor(criteria.params, memberTotal) : of(null))),
      catchError(() => of(null))
    );
  }

  /**
   * "No phone number" covers a missing number and an empty one, as the
   * member rows do ("No number"). The three counts must add up to the
   * group's total, or nothing is shown.
   */
  private reachFor(filter: Record<string, unknown>, memberTotal: number): Observable<Reach | null> {
    return forkJoin({
      withNumber: this.countContacts(
        { ...filter, 'primaryContactNumber.specified': true },
        { 'primaryContactNumber.notEquals': '' }
      ),
      missing: this.countContacts({ ...filter, 'primaryContactNumber.specified': false }),
      blank: this.countContacts(filter, { 'primaryContactNumber.equals': '' }),
    }).pipe(
      map(({ withNumber, missing, blank }) =>
        withNumber + missing + blank === memberTotal ? { withNumber, withoutNumber: missing + blank } : null
      ),
      catchError(() => of(null))
    );
  }

  // ── Contact list counts ──

  /**
   * How many contacts match a contact list query (one row requested, total
   * read). `exact` params are sent as they are, empty strings included.
   */
  countContacts(params: Record<string, unknown>, exact: Record<string, string> = {}): Observable<number> {
    return this.api
      .get<any>(
        `${SERVICE}/api/v2/person-entities/organisations/${this.org}/contacts`,
        { ...clean({ ...params, page: 0, size: 1 }), ...exact }
      )
      .pipe(map((data) => Number(data?.itemPagination?.totalElements) || 0));
  }
}
